<script setup lang="ts">
import Button from "../../base-components/Button";
import {FormTextarea} from "../../base-components/Form";
import Lucide from "../../base-components/Lucide";
import {Ref, onBeforeMount, ref, nextTick, watch, onMounted} from "vue";
import {userUserStore} from "../../stores/user";
import ModalChatAbrirImagen from "./modals/ModalChatAbrirImagen.vue";
import MobileDetect from "mobile-detect";
import {ChatMensaje, EnviarMensajeChat, chatConverter, utilFechaGetCurrentTime} from "@fixeduy/fixedtypes";
import {collection, doc, getDocs, getFirestore, query, setDoc, where, writeBatch} from "firebase/firestore";
import {getDownloadURL, getStorage, ref as storageRef, uploadBytes} from "firebase/storage";
import router from "../../router";
import {RouteRecordName} from "vue-router";
import {enviarMensajeChat} from "../../services/cloudFunctions/enviarMensajeChat";
import ChatVentanaMensajeCard from "./ChatVentanaMensajeCard.vue";

const userStore = userUserStore();
const formMensaje = ref("");
const db = getFirestore();
const autorUserId = userStore.userLoggedIn?.uid ?? "";
const autorNombre = userStore.usuario?.nombre ?? "";
const archivoAdjunto: Ref<any> = ref(null);
const refAdjunto = ref("");
const storage = getStorage();
const cargando = ref(false);
const enChat = ref(false);
const paginaActual: Ref<RouteRecordName | null | undefined> = ref(null);
const mostrarNombre = ref(false);

const emit = defineEmits<{
  (e: "cerrar"): void;
  (e: "cargar"): void;
}>();

enum showModalType {
  none,
  AbrirImagen
}

const obtenerInfoDispositivo = () => {
  const md = new MobileDetect(window.navigator.userAgent);
  const dispositivo = md.tablet() ? "Tablet" : md.mobile() ? "Celular" : "Computadora";

  const navegador = (() => {
    const ua = window.navigator.userAgent;
    if (/chrome|chromium|crios/i.test(ua)) return "Chrome";
    if (/firefox|fxios/i.test(ua)) return "Firefox";
    if (/safari/i.test(ua) && !/chrome|crios/i.test(ua)) return "Safari";
    if (/msie|trident/i.test(ua)) return "Internet Explorer";
    if (/edge/i.test(ua)) return "Edge";
    return "Navegador desconocido";
  })();

  return {
    dispositivo,
    navegador
  };
};

onBeforeMount(async () => {
  router.beforeEach(async (to, from) => {
    const currentRouteName = to.name;
    paginaActual.value = currentRouteName;
  });

  const infoDispositivo = obtenerInfoDispositivo();
  const chatDocRef = doc(db, `chats/${userStore.empresa?.id}-${autorUserId}`).withConverter(chatConverter);

  await setDoc(
    chatDocRef,
    {
      dispositivo: infoDispositivo.dispositivo,
      navegador: infoDispositivo.navegador
    },
    {merge: true}
  );
});

const showModal = (imagen: any, modalShow: showModalType) => {
  modal.value = modalShow;
  imagenSeleccionada.value = imagen;
};

const cerrarModal = () => {
  modal.value = showModalType.none;
  imagenSeleccionada.value = null;
};

const modal = ref<showModalType>(showModalType.none);
const imagenSeleccionada = ref<any | null>(null);

const generateRandomHash = (length: number): string => {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.-_";
  let result = "";
  const charactersLength: number = characters.length;

  for (let i = 0; i < length; i++) {
    const randomIndex: number = Math.floor(Math.random() * charactersLength);
    result += characters.charAt(randomIndex);
  }

  return result;
};

const enviarMensaje = async () => {
  // return;
  if (!cargando.value && (formMensaje.value != "" || archivoAdjunto.value)) {
    cargando.value = true;
    let urlAdjunto = "";
    if (archivoAdjunto.value) {
      const extension = archivoAdjunto.value.name.split(".").pop() ?? "";
      const nombre = archivoAdjunto.value.name.replace(`.${extension}`, "");
      const extensionMin = extension.toLowerCase();
      const hash = generateRandomHash(8);

      const archivoRef = storageRef(storage, `chat/${userStore.user?.uid}/${nombre}_${hash}.${extensionMin}`);

      const snapshot = await uploadBytes(archivoRef, archivoAdjunto.value);
      refAdjunto.value = snapshot.ref.fullPath;

      urlAdjunto = await getDownloadURL(storageRef(storage, refAdjunto.value));
    }
    const mensaje: ChatMensaje = {
      id: "",
      enviadoPorFixed: false,
      autorNombre: autorNombre,
      autorUserId: autorUserId,
      fecha: utilFechaGetCurrentTime(),
      mensaje: formMensaje.value,
      vistoCliente: true,
      vistoFixed: {},
      notaPrivada: false,
      empresaId: userStore.empresa?.id ?? "",
      urlImagenRemitente: userStore.user?.photoURL ?? "",
      refAdjunto: refAdjunto.value,
      urlAdjunto: urlAdjunto,
      nombreAdjunto: archivoAdjunto.value != null ? archivoAdjunto.value.name : "",
      tamanioAdjunto: archivoAdjunto.value != null ? String(archivoAdjunto.value.size) : "",
      resuelto: null,
      reabierto: null,
      clienteId: autorUserId,
      enviadoDesde: "web"
    };

    const enviarMensaje: EnviarMensajeChat = {
      mensaje: mensaje,
      nombreUsuario: autorNombre,
      nombreEmpresa: userStore.empresa?.nombreComercial ?? "",
      fotoUsuario: userStore.user?.photoURL ?? ""
    };

    await enviarMensajeChat(enviarMensaje);

    formMensaje.value = "";
    archivoAdjunto.value = null;
    refAdjunto.value = "";
    cargando.value = false;
  }
};

onMounted(() => {
  nextTick(() => {
    if (chatContainer.value) {
      chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
    }
  });
});

const chatContainer: Ref<HTMLElement | null> = ref(null);
let oldScrollHeight = 0;
let oldScrollTop = 0;
let distanceToBottom = 0;

const scrollToBottomChat = () => {
  nextTick(() => {
    if (chatContainer.value) {
      chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
    }
  });
};

onMounted(() => {
  if (chatContainer.value) {
    chatContainer.value.addEventListener("scroll", onScroll);
  }
});

function onScroll(event: any) {
  if (chatContainer.value != null) {
    const scrollTop = chatContainer.value.scrollTop;
    distanceToBottom =
      chatContainer.value.scrollHeight - (chatContainer.value.scrollTop + chatContainer.value.clientHeight);
    if (scrollTop <= 60) {
      if (chatContainer.value != null) {
        oldScrollHeight = chatContainer.value.scrollHeight;
        oldScrollTop = chatContainer.value.scrollTop;
      }
      userStore.cargarMensajesChat();
    }
  }
}

const onResize = () => {
  if (chatContainer.value != null) {
    // Calculo los nuevos valores:
    const currentScrollHeight = chatContainer.value.scrollHeight;
    const newScrollTop = oldScrollTop + currentScrollHeight - oldScrollHeight;
    nextTick(() => {
      if (chatContainer.value) {
        chatContainer.value.scrollTop = newScrollTop;
      }
    });
  }
};

watch(
  () => userStore.chatMensajes.length,
  (newLength, oldLength) => {
    if (oldScrollHeight == 0 || distanceToBottom <= 60) {
      scrollToBottomChat();
    }
  }
);

const adjuntarArchivo = (event: any) => {
  const file = event.target.files?.[0];
  console.log(file);
  if (file) {
    archivoAdjunto.value = file;
  }
};

const borrarAdjunto = () => {
  archivoAdjunto.value = null;
};

const pegarDesdePortapapeles = (event: ClipboardEvent) => {
  const items = Array.from(event.clipboardData?.items || []);

  items.forEach(item => {
    if (item.type.startsWith("image/")) {
      const blob = item.getAsFile();
      if (blob) {
        const reader = new FileReader();
        reader.onload = async e => {
          const base64Image = e.target?.result as string;
          const file = dataURItoFile(base64Image, "pasted_image.png");
          archivoAdjunto.value = file;
        };
        reader.readAsDataURL(blob);
      }
    }
  });
};

function dataURItoFile(dataURI: string, filename: string): File {
  const arr = dataURI.split(",");
  const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, {type: mime});
}

const descargarArchivo = async (refAdjunto: string, nombre: string) => {
  try {
    const downloadURL = await getDownloadURL(storageRef(storage, refAdjunto));

    // Fetch the file as a Blob so we can force the download
    const response = await fetch(downloadURL);
    const blob = await response.blob();

    // Create a link to download the Blob
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = nombre;

    // Append the link to the body (necessary for Firefox)
    document.body.appendChild(link);

    // Programmatically click the link to trigger the download
    link.click();

    // Clean up by revoking the object URL and removing the link
    window.URL.revokeObjectURL(link.href);
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

watch(
  () => enChat.value,
  newValue => {
    if (enChat.value) {
      actualizarVistoMensajes(userStore.empresa?.id!, autorUserId);
      actualizarVisto(userStore.empresa?.id!, autorUserId);
      if (formMensaje.value != "") {
        actualizarEscribiendoTrue(userStore.empresa?.id!, autorUserId);
      } else {
        actualizarEscribiendoFalse(userStore.empresa?.id!, autorUserId);
      }
    } else {
      actualizarEscribiendoFalse(userStore.empresa?.id!, autorUserId);
    }
  }
);

// watch(
//   () => formMensaje.value,
//   newValue => {
//     if (enChat.value) {
//       actualizarVistoMensajes(userStore.empresa?.id!, autorUserId);
//       actualizarVisto(userStore.empresa?.id!, autorUserId);
//       if (formMensaje.value != "") {
//         actualizarEscribiendoTrue(userStore.empresa?.id!, autorUserId);
//       } else {
//         actualizarEscribiendoFalse(userStore.empresa?.id!, autorUserId);
//       }
//     } else {
//       actualizarEscribiendoFalse(userStore.empresa?.id!, autorUserId);
//     }
//   }
// );

const actualizarVistoMensajes = async (idEmpresa: string, idChat: string) => {
  const q = query(collection(db, `chats/${idEmpresa}-${idChat}/mensajes`), where("vistoCliente", "==", false));

  getDocs(q).then(querySnapshot => {
    const batch = writeBatch(db);
    querySnapshot.forEach(doc => {
      batch.update(doc.ref, {vistoCliente: true});
    });
    batch.commit();
  });

  return true;
};

const actualizarVisto = async (idEmpresa: string, idChat: string) => {
  if (userStore.chatMensajes.length > 0) {
    const docChatRef = doc(db, `chats/${idEmpresa}-${idChat}`).withConverter(chatConverter);
    setDoc(
      docChatRef,
      {
        sinLeerCliente: false
      },
      {merge: true}
    );
  }
};
const actualizarEscribiendoTrue = async (idEmpresa: string, idChat: string) => {
  if (userStore.chatMensajes.length > 0) {
    const docChatRef = doc(db, `chats/${idEmpresa}-${idChat}`).withConverter(chatConverter);
    setDoc(
      docChatRef,
      {
        escribiendoCliente: true
      },
      {merge: true}
    );
  }
};

const actualizarEscribiendoFalse = async (idEmpresa: string, idChat: string) => {
  if (userStore.chatMensajes.length > 0) {
    const docChatRef = doc(db, `chats/${idEmpresa}-${idChat}`).withConverter(chatConverter);
    setDoc(
      docChatRef,
      {
        escribiendoCliente: false
      },
      {merge: true}
    );
  }
};
const handleKeydown = (event: any) => {
  if (event.key === "Enter" && event.shiftKey) {
    event.preventDefault();
    formMensaje.value += "\n";
  } else if (event.key === "Enter") {
    event.preventDefault();
    enviarMensaje();
  }
};
</script>

<template>
  <!-- BEGIN: Chat Content -->
  <div
    :class="{
      'fixed bottom-3 z-[100] min-h-[400px] box': true,
      'right-3': userStore.alineacionChat == 'derecha',
      'left-3': userStore.alineacionChat == 'izquierda',
      'w-[35%] h-[calc(100vh-100px)] min-w-[300px]': userStore.tamanoChat == 'grande',
      'w-[35%] h-[500px] min-w-[300px]': userStore.tamanoChat == 'chico'
    }"
  >
    <div class="flex pt-2 px-3">
      <div class="flex-1">
        <h4 class="text-xl font-medium leading-none mt-2">Chat de ayuda</h4>
      </div>
      <div class="flex-none w-14">
        <Button variant="secondary" @click="userStore.toggleTamano">
          <Lucide :icon="userStore.tamanoChat == 'chico' ? 'ZoomIn' : 'ZoomOut'" class="w-5 h-5" />
        </Button>
      </div>
      <div class="flex-none w-14">
        <Button variant="secondary" @click="userStore.toggleAlineacion">
          <Lucide :icon="userStore.alineacionChat == 'izquierda' ? 'ArrowRight' : 'ArrowLeft'" class="w-5 h-5" />
        </Button>
      </div>
      <div class="flex-none w-10">
        <Button variant="secondary" @click="() => userStore.cerrarChat()">
          <Lucide icon="ChevronDown" class="w-5 h-5" />
        </Button>
      </div>
    </div>
    <hr class="mt-3" />
    <!-- BEGIN: Chat Active -->
    <div class="flex flex-col h-full">
      <div
        class="flex-1 px-3 overflow-y-auto scrollbar-thin scrollbar-thumb-primary scrollbar-track-gray-100 dark:scrollbar-track-gray-800 dark:scrollbar-thumb-primary-dark"
        style="max-height: calc(100% - 180px)"
        ref="chatContainer"
      >
        <DynamicScroller
          class="scroller"
          :min-item-size="25"
          :items="userStore.chatMensajes"
          @scroll="onScroll"
          @resize="onResize"
        >
          <template v-slot="{item, index, active}">
            <DynamicScrollerItem :item="item" :active="active" :size-dependencies="[item.message]" :data-index="index">
              <ChatVentanaMensajeCard
                :chat="item"
                :mostrarNombre="mostrarNombre"
                @show-modal="showModal"
                @descargar-archivo="descargarArchivo"
              />
            </DynamicScrollerItem>
          </template>
        </DynamicScroller>
      </div>

      <div class="w-full flex border-t border-slate-200/60 dark:border-darkmode-400 p-2" v-if="archivoAdjunto != null">
        <span
          v-if="archivoAdjunto"
          class="ms-1 text-blue-500"
          style="max-width: 280px; overflow: hidden; text-overflow: ellipsis; cursor: pointer"
        >
          {{ archivoAdjunto.name }}
        </span>
        <span style="cursor: pointer" @click="borrarAdjunto" class="text-danger"
          ><Lucide icon="Trash2Icon" class="ml-2" />
        </span>
      </div>
      <!-- END: Chat Active -->
      <!-- BEGIN: Send new chat -->
      <div class="w=full flex items-center pt-2 border-t border-slate-200/60 dark:border-darkmode-400 mt-2">
        <!-- v-if="!cargando" -->
        <FormTextarea
          class="px-2 py-3 border-transparent shadow-none resize-none h-[100%] dark:bg-darkmode-600 focus:border-transparent focus:ring-0"
          :rows="4"
          placeholder="Escribe un mensaje..."
          v-model="formMensaje"
          @keydown="handleKeydown"
          @paste="pegarDesdePortapapeles"
          @focus="enChat = true"
          @blur="enChat = false"
        />
        <div class="relative w-4 h-4 mr-3 sm:w-5 sm:h-5 text-slate-1000 sm:mr-5 mb-12">
          <Lucide icon="Paperclip" class="w-full h-full" />
          <input type="file" class="absolute top-0 left-0 w-full h-full opacity-0" @change="adjuntarArchivo" />
        </div>
        <a
          href="#"
          class="flex items-center justify-center flex-none w-8 h-8 mr-5 text-white rounded-full sm:w-10 sm:h-10 bg-primary mb-12"
        >
          <Lucide icon="Send" class="w-4 h-4" @click="enviarMensaje" />
        </a>
      </div>
      <!-- <div v-else>
        <div
          class="flex items-center px-5 py-4 border-t border-slate-200/60 dark:border-darkmode-400"
        >
          <div class="mx-auto">
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
      </div> -->
    </div>

    <!-- END: Send new chat -->
  </div>
  <!-- END: Chat Content -->
  <ModalChatAbrirImagen
    v-if="modal == showModalType.AbrirImagen && imagenSeleccionada != null"
    :imagen="imagenSeleccionada"
    @cerrar="cerrarModal"
    @cargar="() => emit('cargar')"
  />
</template>
<style scoped>
.break-words {
  overflow-wrap: break-word;
  word-break: break-word;
}
.break-all {
  word-break: break-all;
}
</style>
